import { z } from 'zod';

import { apiClient } from './client';

/**
 * Chat API (web) — модель «беседы», зеркалит backend Agent Z и source/mobile.
 *
 * Раньше веб бил в несуществующий `/messages/{leaseId}` с полями
 * `sender_id`/`body` -> чат был полностью мёртв (404 + Zod-разъезд). Реальный
 * контракт:
 *   POST /api/chat/conversations            { lease_id }  (идемпотентно: 200|201)
 *   GET  /api/chat/conversations/{id}/messages?before=  (DESC, лимит 50)
 *   POST /api/chat/conversations/{id}/messages { content }
 *   POST /api/chat/conversations/{id}/read
 * Realtime: private-канал `chat.conversation.{id}`, событие `.new-message`.
 */

export const ChatAttachmentZ = z.object({
  path: z.string(),
  url: z.string(),
  mime_type: z.string().optional(),
  size: z.number().optional(),
});
export type ChatAttachment = z.infer<typeof ChatAttachmentZ>;

export const ChatMessageZ = z.object({
  id: z.number().int(),
  conversation_id: z.number().int(),
  // backend ChatMessageResource: sender_user_id (НЕ sender_id), content (НЕ body).
  sender_user_id: z.number().int(),
  content: z.string(),
  attachments: z.array(ChatAttachmentZ).default([]),
  read_at: z.string().nullable().optional(),
  created_at: z.string(),
});
export type ChatMessage = z.infer<typeof ChatMessageZ>;

export const ChatConversationZ = z.object({
  id: z.number().int(),
  lease_id: z.number().int().nullable().optional(),
  unread_count: z.number().int().nonnegative().default(0),
  updated_at: z.string().optional(),
});
export type ChatConversation = z.infer<typeof ChatConversationZ>;

const ConversationResourceZ = z.object({ data: ChatConversationZ });
const MessagesCollectionZ = z.object({ data: z.array(ChatMessageZ) });
const MessageResourceZ = z.object({ data: ChatMessageZ });

/** Get-or-create беседу по lease_id (бэкенд идемпотентен). */
export async function createConversation(leaseId: number): Promise<ChatConversation> {
  const res = await apiClient.post('/chat/conversations', { lease_id: leaseId });
  return ConversationResourceZ.parse(res.data).data;
}

/**
 * Сообщения беседы. Бэкенд отдаёт DESC (новые сверху) -> разворачиваем в ASC
 * для bottom-anchored вью (старые сверху, новые снизу).
 */
export async function listMessages(conversationId: number): Promise<ChatMessage[]> {
  const res = await apiClient.get(`/chat/conversations/${conversationId}/messages`);
  return MessagesCollectionZ.parse(res.data).data.slice().reverse();
}

export async function sendMessage(conversationId: number, content: string): Promise<ChatMessage> {
  const res = await apiClient.post(`/chat/conversations/${conversationId}/messages`, { content });
  return MessageResourceZ.parse(res.data).data;
}

export async function markConversationRead(conversationId: number): Promise<void> {
  await apiClient.post(`/chat/conversations/${conversationId}/read`);
}

export const chatApi = {
  createConversation,
  listMessages,
  sendMessage,
  markRead: markConversationRead,
};
